/* eslint-disable no-console */
import { useLazyQuery, useMutation } from '@apollo/client';
import { User } from '@firebase/auth';
import { useCallback, useEffect, useState } from 'react';

import { client } from '../data/GraphQL/api/client';
import {
  CheckMyPinCodeDocument,
  CurrentHealthProfessionalItemFragment,
  GetCurrentHealthProfessionalDocument,
  HealthProfessionalForgotPinCodeDocument,
  PinCodeResult,
  UpdateHealthProfessionalItemPinCodeDocument,
} from '../data/types/api/graphql';
import {
  auth,
  AuthStatus,
  changePassword,
  enrolledMFA,
  getExpirationTime,
  IAuthenticationStatus,
  IPinCodeStatus,
  IVerification,
  loginWithEmailAndPassword,
  reauthenticate,
  resetPassword,
  resetPasswordWithEmail,
  verifyPhoneCode,
} from '../firebase/authentication';

const useAuth = () => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loginStatus, setLoginStatus] = useState<IAuthenticationStatus | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const [me, setMe] = useState<CurrentHealthProfessionalItemFragment | null>(
    null
  );
  const [getAccount] = useLazyQuery(GetCurrentHealthProfessionalDocument);
  const [checkMyPinCode] = useLazyQuery(CheckMyPinCodeDocument);

  const [updateHealthProfesionalItemPinCode] = useMutation(
    UpdateHealthProfessionalItemPinCodeDocument,
    {
      refetchQueries: [GetCurrentHealthProfessionalDocument],
    }
  );

  const [healthProfessionalForgotPinCode] = useMutation(
    HealthProfessionalForgotPinCodeDocument
  );

  const signIn = useCallback(
    async (username: string, password: string) => {
      let status = {
        status: AuthStatus.UNAUTHORIZED,
      };

      if (!authUser) {
        setLoading(true);
        status = await loginWithEmailAndPassword(username, password);
        setLoginStatus(status);
        setLoading(false);
      }

      return status;
    },
    [authUser]
  );

  const signOut = useCallback(async () => {
    setLoading(true);

    await auth.signOut();

    setToken(null);
    setAuthUser(null);
    setMe(null);
    setValidating(false);

    await client.clearStore();
    localStorage.clear();
    setLoginStatus(null);
    setLoading(false);
  }, []);

  const setIsUnauthorized = useCallback(async () => {
    await signOut();
    setLoginStatus({
      status: AuthStatus.UNAUTHENTICATED,
    });
  }, [signOut]);

  const forgotPassword = useCallback(async (email: string) => {
    const passwordStatus = await resetPasswordWithEmail(email);
    return passwordStatus;
  }, []);

  const recoverPassword = useCallback(
    async (resetCode: string, password: string, confirmPasssword: string) => {
      const passwordStatus = await resetPassword(
        resetCode,
        password,
        confirmPasssword
      );
      return passwordStatus;
    },
    []
  );

  const checkOOBCode = useCallback(async (oobCode: string) => {
    const codeUrl = `${import.meta.env.VITE_GRAPHQL_API?.replace(
      'graphql',
      'CheckPasswordReset'
    )}${oobCode}`;
    try {
      const response = await fetch(codeUrl);
      const data = (await response.json()) as boolean;
      return data;
    } catch (e) {
      return false;
    }
  }, []);

  const verifyWithPassword = useCallback(
    async (currentPassword: string) => reauthenticate(currentPassword),
    []
  );

  const setPassword = useCallback(
    async (password: string) => changePassword(password),
    []
  );

  // verify sms code for login or update
  const verifySMSCode = useCallback(
    async (code: string, verification?: IVerification) => {
      let result: IAuthenticationStatus = {
        status: AuthStatus.UNVERIFIED,
        message: undefined,
      };
      let isLogin = false;
      let currentVerification: IVerification | null = null;

      // get login status verification
      if (loginStatus?.verification) {
        currentVerification = loginStatus.verification;
        isLogin = true;
      }

      // overide with verification
      if (verification) {
        currentVerification = verification;
      }
      // update loader for login
      if (isLogin) {
        setLoading(true);
      }

      if (currentVerification) {
        result = await verifyPhoneCode(code, currentVerification);
        // update loginstatus if isLogin
        if (isLogin) {
          setLoginStatus(result);
          if (result.status === AuthStatus.PINCODE) {
            localStorage.setItem('pincode', 'true');
          }
        }

        // update result if not login and status is pincode, return verified instead of pincode
        if (!isLogin && result.status === AuthStatus.PINCODE) {
          result = { ...result, status: AuthStatus.VERIFIED };
        }
      }

      return result;
    },
    [loginStatus?.verification]
  );

  const checkPinCode = useCallback(
    async (code: string) => {
      setValidating(true);
      setLoading(true);

      let status: IAuthenticationStatus = {
        status: AuthStatus.UNAUTHORIZED,
      };

      localStorage.removeItem('pincodeid');

      const result = await checkMyPinCode({
        fetchPolicy: 'network-only',
        variables: { pinCode: code },
      });

      if (result.data?.pinCodeCheck.result) {
        switch (result.data?.pinCodeCheck.result) {
          case PinCodeResult.Valid:
            // authorized + path
            status = {
              status: AuthStatus.AUTHORIZED,
              path: localStorage.getItem('idle') ?? undefined,
            };
            localStorage.setItem(
              'pincodeid',
              result.data?.pinCodeCheck.id ?? ''
            );
            localStorage.removeItem('idle');
            localStorage.setItem('pincode', 'false');
            localStorage.setItem('language', 'nl');
            break;
          case PinCodeResult.AccountNotFound:
            status = {
              status: AuthStatus.PINCODE,
              message: 'user-not-found',
            };
            break;
          case PinCodeResult.Invalid:
            status = {
              status: AuthStatus.PINCODE,
              message: 'invalid-pincode',
            };
            break;
          case PinCodeResult.TooManyTries:
            status = {
              status: AuthStatus.PINCODE,
              message: 'too-many-requests',
            };
            break;
          default:
            status = {
              status: AuthStatus.PINCODE,
              message: 'invalid-pincode',
            };
        }
      }

      setLoginStatus(status);
      setLoading(false);
      setValidating(false);
      return status;
    },
    [checkMyPinCode]
  );

  const forgotPinCode = useCallback(async () => {
    const { data } = await healthProfessionalForgotPinCode({
      fetchPolicy: 'network-only',
    });
    const success = data?.pinCodeForgot.boolean ?? false;
    const status: IPinCodeStatus = {
      success,
      message: !success ? 'unknown-error' : undefined,
    };
    return status;
  }, [healthProfessionalForgotPinCode]);

  const setPinCode = useCallback(
    async (pinCode: string, currentPinCode?: string, reloadUser?: boolean) => {
      let status: IPinCodeStatus = { success: false };
      const result = await updateHealthProfesionalItemPinCode({
        fetchPolicy: 'network-only',
        variables: { oldPinCode: currentPinCode, pinCode },
      });
      const success = result.data?.pinCodeSet?.boolean ?? false;
      const message = result?.data?.pinCodeSet?.errors
        ?.map(x => x.message)
        .join(',');
      status = { success, message };

      // get user to reload haspincode
      if (success && reloadUser) {
        const { data: refetchUser } = await getAccount({
          fetchPolicy: 'network-only',
        });
        if (refetchUser?.healthProfessionalMe) {
          if (!refetchUser.healthProfessionalMe.hasDashboardAccess) {
            await setIsUnauthorized();
          } else {
            setMe(refetchUser?.healthProfessionalMe);
          }
        }
      }
      return status;
    },
    [getAccount, setIsUnauthorized, updateHealthProfesionalItemPinCode]
  );

  // pincode needed
  const setIdle = useCallback((path?: string) => {
    setLoginStatus({ status: AuthStatus.PINCODE });
    localStorage.setItem('idle', path ?? '');
    localStorage.setItem('pincode', 'true');
  }, []);

  const updateToken = useCallback(async (user: User | null) => {
    const newToken = (await user?.getIdToken()) ?? null;

    if (newToken && localStorage.getItem('token') !== newToken) {
      const expirationTime = getExpirationTime(newToken);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('expirationTime', expirationTime.toString());
    }
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async user => {
      if (user) {
        await updateToken(user);
      }
    });
    return () => unsubscribe();
  }, [updateToken]);

  // FIREBASE AUTH CALLBACK
  useEffect(() => {
    const unlisten = auth.onAuthStateChanged(async user => {
      setLoading(true);

      // set token
      await updateToken(user);

      setInitialized(true);

      if (user && !validating && !me) {
        setValidating(true);

        // refresh account network only + me check to disable cache;
        const { data } = await getAccount({ fetchPolicy: 'network-only' });

        if (data?.healthProfessionalMe) {
          // user has no access
          if (!data.healthProfessionalMe.hasDashboardAccess) {
            await setIsUnauthorized();
          } else {
            setMe(data.healthProfessionalMe);

            // CHECK IF ENROLLED MFA
            const enrolled = enrolledMFA();

            // check expiration
            const expirationTime = localStorage.getItem('expirationTime');
            const remainingTime = expirationTime
              ? new Date(parseInt(expirationTime)).getTime() -
                new Date().getTime()
              : -1;
            const expired = expirationTime
              ? parseInt(expirationTime) - new Date().getTime() < 0
              : true;
            console.log(
              expirationTime
                ? `[AUTH] expired : ${expired} - ${new Date(
                    parseInt(expirationTime)
                  )} / remaining ${remainingTime}`
                : 'no expiration time'
            );

            // AUTO SET PINCODE
            if (!localStorage.getItem('pincode') || expired) {
              localStorage.setItem('pincode', 'true');
            }

            // check idle
            const idle = localStorage.getItem('pincode') === 'true';

            let status = AuthStatus.UNAUTHORIZED;
            if (!enrolled) {
              // show phone authentication
              status = AuthStatus.UNVERIFIED;
            } else {
              // show pincode or go to dashboard
              status = idle ? AuthStatus.PINCODE : AuthStatus.AUTHORIZED;
            }

            setLoginStatus({
              status,
            });

            setLoading(false);
            setValidating(false);
          }
        } else {
          // no user
          await setIsUnauthorized();
        }
      } else {
        setLoading(false);
      }
      // update user
      setAuthUser(user);
    });

    return () => {
      unlisten();
    };
  }, [
    authUser,
    getAccount,
    me,
    setIsUnauthorized,
    signOut,
    updateToken,
    validating,
  ]);

  // Refetch van current user werkt niet
  const reloadMe = useCallback(async () => {
    const { data } = await getAccount();
    if (data?.healthProfessionalMe) {
      setMe(data.healthProfessionalMe);
    }
  }, [getAccount]);

  return {
    authUser,
    token,
    signIn,
    signOut,
    verifySMSCode,
    checkPinCode,
    checkOOBCode,
    setPinCode,
    forgotPinCode,
    forgotPassword,
    recoverPassword,
    verifyWithPassword,
    setPassword,
    setIdle,
    loginStatus,
    loading,
    validating,
    initialized,
    me,
    reloadMe,
  };
};

export { useAuth };
